// =============================================================================
// features/admin/document-types/DocumentTypeModal.tsx — Modal create / edit de
// tipo de documento (F46-S12).
//
// DS:
//   - box-shadow: var(--elev-5) — hierarquia de modal (DS §9.6)
//   - Animação entrada: fade-up 300ms cubic-bezier(0.16,1,0.3,1) (igual CityFormModal)
//   - Portal: createPortal(…, document.body)
//   - Form: React Hook Form + zodResolver(DocumentTypeCreateSchema) — schema
//     compartilhado de @elemento/shared-schemas (F46-S02), nunca redigitado.
//   - 409 (key duplicada) → erro inline no campo `key`.
//
// Regras de negócio do slot:
//   - `key` é imutável após criação — campo desabilitado em modo edição.
//   - Não existe excluir — o toggle `active` é a única forma de "remover" um
//     tipo (ele fica fora do seletor de upload, mas documentos já
//     classificados continuam apontando para ele).
//   - Marcar qualquer item em `required_for` muda o checklist de TODAS as
//     análises/contratos abertos daquele fluxo — aviso sempre visível
//     enquanto houver pelo menos um fluxo marcado (não é um confirm único,
//     é um lembrete permanente enquanto o campo estiver "ligado").
//   - Ordenação por `sort_order` é campo numérico simples — arrastar não cabe
//     no orçamento deste slot (DS: "escolha a forma mais simples").
//
// Props:
//   - Sem documentType → modo create (POST)
//   - Com documentType → modo edit (PATCH, key read-only)
// =============================================================================

import type {
  DocumentType,
  DocumentTypeCategory,
  DocumentTypeCreate,
  DocumentTypeRequiredFor,
  DocumentTypeUpdate,
} from '@elemento/shared-schemas';
import {
  DocumentTypeCreateSchema,
  DEFAULT_DOCUMENT_ACCEPTED_MIMES,
} from '@elemento/shared-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Select } from '../../../components/ui/Select';
import { cn } from '../../../lib/cn';

import { useCreateDocumentType, useUpdateDocumentType } from './hooks';

// ─── Domínios fechados → rótulos pt-BR ────────────────────────────────────────

export const CATEGORY_OPTIONS: { value: DocumentTypeCategory; label: string }[] = [
  { value: 'identificacao', label: 'Identificação' },
  { value: 'renda', label: 'Renda' },
  { value: 'endereco', label: 'Endereço' },
  { value: 'negocio', label: 'Negócio' },
  { value: 'garantia', label: 'Garantia' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'outros', label: 'Outros' },
];

export const REQUIRED_FOR_OPTIONS: { value: DocumentTypeRequiredFor; label: string }[] = [
  { value: 'credit_analysis', label: 'Análise de crédito' },
  { value: 'contract', label: 'Contrato' },
];

export const ACCEPTED_MIME_OPTIONS: { value: string; label: string }[] =
  DEFAULT_DOCUMENT_ACCEPTED_MIMES.map((mime) => ({
    value: mime,
    label:
      mime === 'application/pdf'
        ? 'PDF'
        : mime === 'image/jpeg'
          ? 'JPEG'
          : mime === 'image/png'
            ? 'PNG'
            : mime === 'image/webp'
              ? 'WebP'
              : mime,
  }));

// ─── Conversões puras (testadas isoladamente — sem render) ───────────────────

/** MB (exibido no form) → bytes (persistido no schema). */
export function mbToBytes(mb: number): number {
  return Math.round(mb * 1_048_576);
}

/** bytes (persistido) → MB (exibido no form), com até 2 casas decimais. */
export function bytesToMb(bytes: number): number {
  return Math.round((bytes / 1_048_576) * 100) / 100;
}

/**
 * Marcar qualquer fluxo em `required_for` muda o checklist de TODAS as
 * análises/contratos ABERTOS daquele fluxo — o aviso fica visível enquanto
 * houver pelo menos um fluxo selecionado.
 */
export function shouldShowRequiredForWarning(requiredFor: DocumentTypeRequiredFor[]): boolean {
  return requiredFor.length > 0;
}

/** Monta o corpo de PATCH a partir do form — `key` nunca é reenviada (imutável). */
export function buildUpdatePayload(form: DocumentTypeCreate): DocumentTypeUpdate {
  const { key: _key, ...rest } = form;
  return rest;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface DocumentTypeModalProps {
  open: boolean;
  onClose: () => void;
  /** Quando presente: modo edit (key vira read-only, PATCH em vez de POST). */
  documentType?: DocumentType | undefined;
}

// ─── Switch reutilizável (mesmo padrão de CityFormModal) ─────────────────────

function ToggleSwitch({
  id,
  checked,
  onChange,
  disabled,
  label,
  labelOn,
  labelOff,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  labelOn: string;
  labelOff: string;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full',
          'border-2 border-transparent',
          'transition-colors duration-fast ease',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
          'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
        style={{ backgroundColor: checked ? 'var(--brand-azul)' : 'var(--surface-muted)' }}
      >
        <span
          className="pointer-events-none block h-4 w-4 rounded-full bg-white transition-transform duration-fast ease"
          style={{
            boxShadow: 'var(--elev-1)',
            transform: checked ? 'translateX(16px)' : 'translateX(0)',
          }}
          aria-hidden="true"
        />
      </button>
      <label
        htmlFor={id}
        className="font-sans text-sm font-medium text-ink-2 cursor-pointer select-none"
      >
        {checked ? labelOn : labelOff}
      </label>
    </div>
  );
}

// ─── Sub-componente de form ───────────────────────────────────────────────────

interface DocumentTypeFormProps {
  documentType?: DocumentType | undefined;
  onClose: () => void;
}

function DocumentTypeForm({ documentType, onClose }: DocumentTypeFormProps): React.JSX.Element {
  const isEditMode = Boolean(documentType);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DocumentTypeCreate>({
    resolver: zodResolver(DocumentTypeCreateSchema),
    defaultValues: documentType
      ? {
          key: documentType.key,
          label: documentType.label,
          category: documentType.category,
          required_for: documentType.required_for,
          expires_after_days: documentType.expires_after_days,
          accepted_mimes: documentType.accepted_mimes,
          max_size_bytes: documentType.max_size_bytes,
          active: documentType.active,
          sort_order: documentType.sort_order,
        }
      : {
          key: '',
          label: '',
          category: 'outros',
          required_for: [],
          expires_after_days: null,
          accepted_mimes: [...DEFAULT_DOCUMENT_ACCEPTED_MIMES],
          max_size_bytes: 10_485_760,
          active: true,
          sort_order: 0,
        },
  });

  const handleConflict = (message: string): void => {
    setError('key', { type: 'manual', message });
  };

  const { mutate: doCreate, isPending: isCreating } = useCreateDocumentType({
    onSuccess: () => onClose(),
    onConflict: handleConflict,
  });

  const { mutate: doUpdate, isPending: isUpdating } = useUpdateDocumentType({
    onSuccess: () => onClose(),
    onConflict: handleConflict,
  });

  const isBusy = isSubmitting || isCreating || isUpdating;
  const requiredFor = watch('required_for') ?? [];
  const expiresAfterDays = watch('expires_after_days');
  const maxSizeBytes = watch('max_size_bytes');

  const onSubmit = (data: DocumentTypeCreate): void => {
    if (isEditMode && documentType) {
      doUpdate({ id: documentType.id, body: buildUpdatePayload(data) });
    } else {
      doCreate(data);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      noValidate
      className="px-6 py-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto"
    >
      {/* Rótulo exibido ao usuário */}
      <Input
        id="doctype-label"
        label="Rótulo exibido"
        placeholder="Ex: Comprovante de residência"
        required
        error={errors.label?.message}
        {...register('label')}
      />

      {/* Key — imutável após criação */}
      <Input
        id="doctype-key"
        label="Chave (key)"
        placeholder="Ex: comprovante_residencia"
        hint={
          isEditMode
            ? 'Imutável após a criação — usada por código e integrações.'
            : 'snake_case, começa com letra. Não pode ser alterada depois de criada.'
        }
        required
        disabled={isEditMode}
        error={errors.key?.message}
        {...register('key')}
      />

      {/* Categoria */}
      <Controller
        name="category"
        control={control}
        render={({ field }) => (
          <Select
            id="doctype-category"
            label="Categoria"
            required
            options={CATEGORY_OPTIONS}
            error={errors.category?.message}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value as DocumentTypeCategory)}
          />
        )}
      />

      {/* Obrigatoriedade — required_for + aviso de impacto */}
      <Controller
        name="required_for"
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            <Label htmlFor="doctype-required-for-0">Exigir para</Label>
            <div className="flex flex-col gap-2">
              {REQUIRED_FOR_OPTIONS.map((opt, i) => {
                const checked = (field.value ?? []).includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    htmlFor={`doctype-required-for-${i}`}
                    className="flex items-center gap-2 font-sans text-sm text-ink-2 cursor-pointer select-none"
                  >
                    <input
                      id={`doctype-required-for-${i}`}
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = field.value ?? [];
                        field.onChange(
                          e.target.checked
                            ? [...current, opt.value]
                            : current.filter((v) => v !== opt.value),
                        );
                      }}
                      className="h-4 w-4 rounded-xs accent-[var(--brand-azul)]"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>

            {shouldShowRequiredForWarning(requiredFor) && (
              <div
                className="flex items-start gap-2 rounded-md px-3 py-2.5"
                role="alert"
                style={{
                  background: 'var(--warning-bg)',
                  borderLeft: '3px solid var(--warning)',
                }}
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  className="w-4 h-4 mt-0.5 shrink-0"
                  style={{ color: 'var(--warning)' }}
                  aria-hidden="true"
                >
                  <path d="M8 1.5l7 12.5H1L8 1.5Z" strokeLinejoin="round" />
                  <path d="M8 6.2v3.2M8 11.8v.3" strokeLinecap="round" />
                </svg>
                <p className="font-sans text-xs text-ink" style={{ lineHeight: 1.5 }}>
                  Tornar este tipo obrigatório muda o checklist de{' '}
                  <strong>todas as análises/contratos abertos</strong> do fluxo marcado, de uma vez
                  — não só os novos.
                </p>
              </div>
            )}
          </div>
        )}
      />

      {/* Mimes aceitos */}
      <Controller
        name="accepted_mimes"
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            <Label htmlFor="doctype-mime-0" required>
              Formatos aceitos
            </Label>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {ACCEPTED_MIME_OPTIONS.map((opt, i) => {
                const checked = (field.value ?? []).includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    htmlFor={`doctype-mime-${i}`}
                    className="flex items-center gap-2 font-sans text-sm text-ink-2 cursor-pointer select-none"
                  >
                    <input
                      id={`doctype-mime-${i}`}
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = field.value ?? [];
                        field.onChange(
                          e.target.checked
                            ? [...current, opt.value]
                            : current.filter((v) => v !== opt.value),
                        );
                      }}
                      className="h-4 w-4 rounded-xs accent-[var(--brand-azul)]"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
            {errors.accepted_mimes?.message && (
              <span role="alert" className="text-xs text-danger">
                {errors.accepted_mimes.message}
              </span>
            )}
          </div>
        )}
      />

      {/* Validade — vence após N dias ou nunca vence */}
      <Controller
        name="expires_after_days"
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            <ToggleSwitch
              id="doctype-expires-toggle"
              checked={expiresAfterDays !== null}
              onChange={(next) => field.onChange(next ? 90 : null)}
              label="Documento tem validade"
              labelOn="Vence após um período"
              labelOff="Não vence"
            />
            {expiresAfterDays !== null && (
              <Input
                id="doctype-expires-days"
                type="number"
                min={1}
                label="Dias de validade a partir da aprovação"
                error={errors.expires_after_days?.message}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? null : Number(e.target.value))
                }
              />
            )}
          </div>
        )}
      />

      {/* Tamanho máximo — exibido em MB, persistido em bytes */}
      <Controller
        name="max_size_bytes"
        control={control}
        render={({ field }) => (
          <Input
            id="doctype-max-size"
            type="number"
            min={1}
            step={0.5}
            label="Tamanho máximo do arquivo (MB)"
            error={errors.max_size_bytes?.message}
            value={bytesToMb(maxSizeBytes ?? field.value)}
            onChange={(e) => {
              const mb = Number(e.target.value);
              field.onChange(Number.isFinite(mb) && mb > 0 ? mbToBytes(mb) : field.value);
            }}
          />
        )}
      />

      {/* Ordem de exibição — campo numérico, sem drag-and-drop (DS: mais simples que cabe) */}
      <Input
        id="doctype-sort-order"
        type="number"
        label="Ordem de exibição"
        hint="Menor número aparece primeiro no checklist e no seletor de upload."
        error={errors.sort_order?.message}
        {...register('sort_order', { valueAsNumber: true })}
      />

      {/* Ativo — nunca existe excluir, só inativar */}
      <Controller
        name="active"
        control={control}
        render={({ field }) => (
          <ToggleSwitch
            id="doctype-active"
            checked={field.value}
            onChange={field.onChange}
            disabled={isBusy}
            label="Tipo ativo no seletor de upload"
            labelOn="Ativo — aparece no seletor de upload"
            labelOff="Inativo — some do seletor, documentos já anexados continuam visíveis"
          />
        )}
      />

      {/* Footer */}
      <div className="flex gap-3 pt-1">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={isBusy}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isBusy} className="flex-1">
          {isBusy
            ? isEditMode
              ? 'Salvando...'
              : 'Criando...'
            : isEditMode
              ? 'Salvar alterações'
              : 'Criar tipo de documento'}
        </Button>
      </div>
    </form>
  );
}

// ─── Modal principal ──────────────────────────────────────────────────────────

/**
 * Modal de criação / edição de tipo de documento.
 * Sem `documentType` → create; com `documentType` → edit (key read-only).
 */
export function DocumentTypeModal({
  open,
  onClose,
  documentType,
}: DocumentTypeModalProps): React.JSX.Element | null {
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const title = documentType ? 'Editar tipo de documento' : 'Novo tipo de documento';

  return createPortal(
    <>
      <div
        role="presentation"
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/20 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="doctype-modal-title"
          className={cn(
            'w-full max-w-lg pointer-events-auto',
            'rounded-lg border border-border',
            'bg-surface-1',
            'animate-[fade-up_300ms_cubic-bezier(0.16,1,0.3,1)_both]',
            'max-h-[calc(100vh-2rem)] overflow-y-auto',
          )}
          style={{ boxShadow: 'var(--elev-5)' }}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle">
            <h2
              id="doctype-modal-title"
              className="font-display font-bold text-ink"
              style={{
                fontSize: 'var(--text-xl)',
                letterSpacing: '-0.03em',
                fontVariationSettings: "'opsz' 24",
              }}
            >
              {title}
            </h2>

            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar modal"
              className={cn(
                'w-8 h-8 flex items-center justify-center',
                'rounded-sm text-ink-3',
                'hover:text-ink hover:bg-surface-hover',
                'transition-all duration-fast ease',
                'focus-visible:ring-2 focus-visible:ring-azul/20',
              )}
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M5 5l10 10M15 5l-10 10" />
              </svg>
            </button>
          </div>

          <DocumentTypeForm documentType={documentType} onClose={onClose} />
        </div>
      </div>
    </>,
    document.body,
  );
}
